import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  erpApi,
  type ErpVerifactuApprovalStatus,
  type ErpVerifactuClient,
  type ErpVerifactuSummary,
} from '../../api/erp';
import { Alert, Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { StatCard } from '../../components/ui/Card';
import { formatDateTime } from '../../utils/format';
import './erp.css';

const APPROVAL_LABEL: Record<ErpVerifactuApprovalStatus, string> = {
  pending: 'Pendiente',
  approved: 'Aprobada',
  rejected: 'Rechazada',
};

const TEST_ONLY_ACCOUNT_WARNING =
  'Esta cuenta no es una cuenta real de cliente. Se aprobará únicamente para realizar pruebas en TEST y no podrá pasar a LIVE. ¿Quieres continuar?';

const LIVE_REAL_CLIENT_ACCOUNT_MESSAGE =
  'Solo se puede pasar a LIVE con cuentas reales de clientes. Las cuentas de administrador y demo pueden utilizarse únicamente en TEST.';

function isRealClientAccount(client: ErpVerifactuClient) {
  return client.account_role === 'user' && !client.is_demo;
}

function approvalTone(status: ErpVerifactuApprovalStatus) {
  if (status === 'approved') return 'success' as const;
  if (status === 'rejected') return 'danger' as const;
  return 'warning' as const;
}

function representationTone(status: ErpVerifactuClient['representacion']) {
  if (status === 'Correcto') return 'success' as const;
  if (status === 'Rechazado') return 'danger' as const;
  return 'warning' as const;
}

export function ErpVerifactuPage() {
  const [clients, setClients] = useState<ErpVerifactuClient[]>([]);
  const [summary, setSummary] = useState<ErpVerifactuSummary | null>(null);
  const [configured, setConfigured] = useState(true);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [entorno, setEntorno] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await erpApi.listVerifactu({ search, entorno, status });
      setClients(result.clients);
      setSummary(result.summary);
      setConfigured(result.configured);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar Verifactu');
    } finally {
      setLoading(false);
    }
  }, [search, entorno, status]);

  useEffect(() => {
    void load();
  }, [load]);

  function replaceClient(client: ErpVerifactuClient) {
    setClients((current) => current.map((item) => (item.id === client.id ? client : item)));
  }

  async function runAction(
    client: ErpVerifactuClient,
    action: () => Promise<{ client: ErpVerifactuClient; message: string }>
  ) {
    setBusyId(client.id);
    setError('');
    setNotice('');
    try {
      const result = await action();
      replaceClient(result.client);
      setNotice(result.message);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo completar la operación');
    } finally {
      setBusyId(null);
    }
  }

  function handleSearch(event: FormEvent) {
    event.preventDefault();
    setSearch(searchDraft.trim());
  }

  function approve(client: ErpVerifactuClient) {
    if (!isRealClientAccount(client)) {
      if (client.entorno !== 'test') {
        window.alert(LIVE_REAL_CLIENT_ACCOUNT_MESSAGE);
        return;
      }
      if (!window.confirm(TEST_ONLY_ACCOUNT_WARNING)) return;
    } else if (
      !window.confirm(`¿Aprobar el alta Verifactu de ${client.business_name} (${client.nif})?`)
    ) {
      return;
    }
    void runAction(client, () => erpApi.setVerifactuApproval(client.id, 'approved'));
  }

  function reject(client: ErpVerifactuClient) {
    const note = window.prompt('Motivo del rechazo (se guardará en el historial):');
    if (note === null) return;
    void runAction(client, () => erpApi.setVerifactuApproval(client.id, 'rejected', note));
  }

  function promote(client: ErpVerifactuClient) {
    if (!isRealClientAccount(client)) {
      window.alert(LIVE_REAL_CLIENT_ACCOUNT_MESSAGE);
      return;
    }
    if (
      !window.confirm(
        `¿Pasar ${client.business_name} (${client.nif}) a LIVE? Esta acción puede generar costes en Verifacti.`
      )
    ) {
      return;
    }
    void runAction(client, () => erpApi.promoteVerifactuLive(client.id));
  }

  return (
    <div>
      <div className="erp-page-heading">
        <div>
          <h1>Verifactu</h1>
          <p className="muted">
            Supervisa las altas de clientes, valida la documentación y controla el paso de test a
            live.
          </p>
        </div>
        <Button variant="secondary" onClick={() => void load()} loading={loading}>
          Actualizar listado
        </Button>
      </div>

      {!configured ? (
        <Alert tone="warning">Verifacti no está configurado en este servidor.</Alert>
      ) : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      {summary ? (
        <div className="erp-stats erp-verifactu-stats">
          <StatCard
            label="Altas"
            value={String(summary.total)}
            hint={`${summary.pending} pendientes`}
          />
          <StatCard label="Aprobadas" value={String(summary.approved)} />
          <StatCard label="En test" value={String(summary.test)} hint="Preparadas para revisar" />
          <StatCard label="En live" value={String(summary.live)} hint="Producción" />
          <StatCard label="Con incidencia" value={String(summary.with_error)} />
        </div>
      ) : null}

      <form className="erp-verifactu-filters" onSubmit={handleSearch}>
        <input
          type="search"
          className="erp-filter-input"
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          placeholder="Buscar por cliente, NIF, negocio o licencia"
          aria-label="Buscar altas Verifactu"
        />
        <select
          className="erp-filter-input"
          value={entorno}
          onChange={(event) => setEntorno(event.target.value)}
          aria-label="Filtrar por entorno"
        >
          <option value="">Todos los entornos</option>
          <option value="test">Test</option>
          <option value="prod">Live</option>
        </select>
        <select
          className="erp-filter-input"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label="Filtrar por aprobación"
        >
          <option value="">Todas las aprobaciones</option>
          <option value="pending">Pendientes</option>
          <option value="approved">Aprobadas</option>
          <option value="rejected">Rechazadas</option>
        </select>
        <Button type="submit" size="sm">Buscar</Button>
      </form>

      {loading && !summary ? <p>Cargando altas Verifactu…</p> : null}
      {!loading && clients.length === 0 ? (
        <div className="erp-empty-state">
          <h2>Sin altas que mostrar</h2>
          <p className="muted">No hay clientes Verifactu que coincidan con los filtros.</p>
        </div>
      ) : null}

      {clients.length > 0 ? (
        <div className="erp-table-wrap">
          <table className="erp-table erp-verifactu-table">
            <thead>
              <tr>
                <th>Cliente y negocio</th>
                <th>Alta</th>
                <th>Verifacti</th>
                <th>Registros</th>
                <th>Última consulta</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => {
                const busy = busyId === client.id;
                const realClientAccount = isRealClientAccount(client);
                const canPromote =
                  realClientAccount &&
                  client.entorno === 'test' &&
                  client.admin_status === 'approved' &&
                  client.representacion === 'Correcto' &&
                  client.remote_active === true;
                return (
                  <tr key={client.id}>
                    <td>
                      <strong>{client.business_name}</strong>
                      <span className="erp-table-secondary">{client.nif}</span>
                      <span className="erp-table-secondary">
                        {client.customer_name} · {client.customer_email}
                      </span>
                      <span className="erp-table-secondary">{client.license_name}</span>
                      {!realClientAccount ? (
                        <div className="erp-badge-stack erp-account-kind-badges">
                          {client.account_role !== 'user' ? (
                            <Badge tone="info">Cuenta {client.account_role}</Badge>
                          ) : null}
                          {client.is_demo ? <Badge tone="warning">Cuenta demo</Badge> : null}
                          <Badge tone="warning">Solo TEST</Badge>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="erp-badge-stack">
                        <Badge tone={approvalTone(client.admin_status)}>
                          {APPROVAL_LABEL[client.admin_status]}
                        </Badge>
                        <Badge tone={client.module_enabled ? 'success' : 'neutral'}>
                          Módulo {client.module_enabled ? 'activo' : 'inactivo'}
                        </Badge>
                      </div>
                      {client.admin_note ? (
                        <span className="erp-table-secondary">{client.admin_note}</span>
                      ) : null}
                    </td>
                    <td>
                      <div className="erp-badge-stack">
                        <Badge tone={client.entorno === 'prod' ? 'success' : 'info'}>
                          {client.entorno === 'prod' ? 'LIVE' : 'TEST'}
                        </Badge>
                        <Badge tone={representationTone(client.representacion)}>
                          Representación: {client.representacion ?? 'sin consultar'}
                        </Badge>
                        <Badge
                          tone={
                            client.remote_active === false
                              ? 'danger'
                              : client.remote_active === true
                                ? 'success'
                                : 'neutral'
                          }
                        >
                          {client.remote_active === false
                            ? 'NIF remoto inactivo'
                            : client.remote_active === true
                              ? 'NIF remoto activo'
                              : 'Estado remoto pendiente'}
                        </Badge>
                      </div>
                      {client.remote_error ? (
                        <span className="erp-table-error">{client.remote_error}</span>
                      ) : null}
                    </td>
                    <td>
                      <strong>{client.records_total}</strong>
                      <span className="erp-table-secondary">
                        {client.records_ok} correctos · {client.records_pending} pendientes
                      </span>
                      {client.records_error > 0 ? (
                        <span className="erp-table-error">{client.records_error} con error</span>
                      ) : null}
                    </td>
                    <td>
                      {client.last_checked_at ? formatDateTime(client.last_checked_at) : 'Nunca'}
                    </td>
                    <td>
                      <div className="erp-row-actions">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy || !configured}
                          onClick={() =>
                            void runAction(client, () => erpApi.refreshVerifactu(client.id))
                          }
                        >
                          Consultar
                        </Button>
                        {client.admin_status !== 'approved' ? (
                          <Button
                            size="sm"
                            disabled={busy || (realClientAccount && client.remote_active !== true)}
                            title={
                              !realClientAccount
                                ? client.entorno === 'test'
                                  ? 'Aprobar únicamente para pruebas en TEST'
                                  : LIVE_REAL_CLIENT_ACCOUNT_MESSAGE
                                : client.remote_active === true
                                ? 'Aprobar el alta'
                                : 'Consulta primero y comprueba que el NIF remoto está activo'
                            }
                            onClick={() => approve(client)}
                          >
                            {realClientAccount ? 'Aprobar alta' : 'Aprobar en test'}
                          </Button>
                        ) : null}
                        {client.admin_status !== 'rejected' && client.entorno === 'test' ? (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={busy}
                            onClick={() => reject(client)}
                          >
                            Rechazar
                          </Button>
                        ) : null}
                        {client.entorno === 'test' ? (
                          <Button
                            size="sm"
                            disabled={busy || (realClientAccount && !canPromote)}
                            title={
                              !realClientAccount
                                ? LIVE_REAL_CLIENT_ACCOUNT_MESSAGE
                                : canPromote
                                ? 'Activar este NIF en producción'
                                : 'Requiere alta aprobada, representación correcta y NIF remoto activo'
                            }
                            onClick={() => promote(client)}
                          >
                            Pasar a live
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
